/**
 * In-memory mock state for the Automations control page.
 * No backend — everything here seeds local React state in AutomationsPage.
 */

export type ChannelId = "messenger" | "instagram" | "whatsapp";

export interface ChannelAutomation {
  id: ChannelId;
  label: string;
  connected: boolean;
  enabled: boolean;
  replyWindowNote: string;
  accent: string; // tailwind token stem, e.g. "channel-messenger"
}

export type ReplyLength = "short" | "medium" | "detailed";
export type Tone = "friendly" | "professional" | "casual";

export interface AiResponseSettings {
  /** When true, the AI sends replies automatically. When false, it saves them
   *  as drafts in the inbox for a human to review/approve before sending. */
  autoReply: boolean;
  tone: Tone;
  language: string;
  autoDetectLanguage: boolean;
  replyLength: ReplyLength;
  fallbackToHuman: boolean;
  confidenceThreshold: number; // 0-100
}

export interface BusinessHoursSettings {
  enabled: boolean;
  hoursLabel: string;
  awayMessage: string;
}

export interface QuickReply {
  id: string;
  shortcut: string;
  message: string;
}

export type RuleAction = "reply" | "tag" | "escalate";

export interface AutomationRule {
  id: string;
  keyword: string;
  action: RuleAction;
  actionDetail: string;
}

export interface KnowledgeBaseStatus {
  productsSynced: number;
  faqDocs: number;
  lastSyncedAt: string;
}

export interface AutomationStats {
  autoHandledPct: number;
  avgResponseTimeSec: number;
  escalationsToHuman: number;
  afterHoursAutoAnswered: number;
}

export const initialStats: AutomationStats = {
  autoHandledPct: 78,
  avgResponseTimeSec: 2.4,
  escalationsToHuman: 14,
  afterHoursAutoAnswered: 132,
};

export const initialChannels: ChannelAutomation[] = [
  {
    id: "messenger",
    label: "Messenger",
    connected: true,
    enabled: true,
    replyWindowNote: "Replies allowed within the 24h Messenger window",
    accent: "channel-messenger",
  },
  {
    id: "instagram",
    label: "Instagram",
    connected: true,
    enabled: false,
    replyWindowNote: "Replies allowed within the 24h Instagram window",
    accent: "channel-instagram",
  },
  {
    id: "whatsapp",
    label: "WhatsApp",
    connected: false,
    enabled: false,
    replyWindowNote: "Replies allowed within the 24h WhatsApp session window",
    accent: "channel-whatsapp",
  },
];

export const initialAiSettings: AiResponseSettings = {
  autoReply: true,
  tone: "friendly",
  language: "English",
  autoDetectLanguage: true,
  replyLength: "medium",
  fallbackToHuman: true,
  confidenceThreshold: 65,
};

export const initialBusinessHours: BusinessHoursSettings = {
  enabled: true,
  hoursLabel: "Mon–Fri, 9:00 AM – 6:00 PM",
  awayMessage:
    "Thanks for reaching out! Our team is offline right now, but our AI assistant can help with orders, product questions, and more. We'll follow up personally first thing tomorrow.",
};

export const initialQuickReplies: QuickReply[] = [
  { id: "qr-1", shortcut: "/greeting", message: "Hi! Thanks for messaging us — how can I help today?" },
  { id: "qr-2", shortcut: "/shipping", message: "Orders ship within 2-3 business days, tracking is emailed automatically." },
  { id: "qr-3", shortcut: "/return", message: "We offer free returns within 14 days of delivery, no questions asked." },
];

export const initialRules: AutomationRule[] = [
  { id: "rule-1", keyword: "refund", action: "escalate", actionDetail: "Escalate to human" },
  { id: "rule-2", keyword: "price", action: "reply", actionDetail: "/shipping" },
  { id: "rule-3", keyword: "wholesale", action: "tag", actionDetail: "Tag as B2B lead" },
];

export const initialKnowledgeBase: KnowledgeBaseStatus = {
  productsSynced: 486,
  faqDocs: 12,
  lastSyncedAt: "3 hours ago",
};
